/**
 * Thin fetch wrapper that:
 *  - always sends HTTP-only cookies (`credentials: 'include'`)
 *  - sets `Content-Type: application/json` for requests with a body
 *  - throws an `ApiError` for non-2xx responses
 *  - on a 401, first tries to renew the session once via
 *    POST /api/auth/refresh (cookie-based, token rotation) and replays the
 *    original request; only when that fails it redirects to `/`
 */

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

type RequestOptions = Omit<RequestInit, "body"> & {
  body?: unknown;
  /**
   * When true, a 401 response does NOT trigger refresh + global redirect to
   * "/". Used by the session probe (GET /api/auth/me), where a 401 simply
   * means "not logged in" and must be handled by the caller instead.
   */
  skipAuthRedirect?: boolean;
};

/**
 * Shared in-flight refresh promise: concurrent 401s (e.g. several react-query
 * calls expiring at once) must trigger exactly one refresh request, since the
 * backend rotates the refresh token — a second parallel refresh with the
 * already-consumed token would fail as revoked.
 */
let refreshPromise: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshPromise ??= fetch("/api/auth/refresh", {
    method: "POST",
    credentials: "include",
  })
    .then((response) => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

async function performRequest(
  path: string,
  options: Omit<RequestOptions, "skipAuthRedirect">,
): Promise<Response> {
  const { body, headers: extraHeaders, ...rest } = options;

  const headers: HeadersInit = {
    ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    ...extraHeaders,
  };

  return fetch(path, {
    ...rest,
    credentials: "include",
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { skipAuthRedirect } = options;

  let response = await performRequest(path, options);

  // Access token expired: try one silent session renewal and replay the
  // request with the fresh cookie before giving up on the session.
  if (response.status === 401 && !skipAuthRedirect) {
    const refreshed = await refreshSession();
    if (refreshed) {
      response = await performRequest(path, options);
    }
  }

  if (response.status === 401) {
    if (skipAuthRedirect) {
      // Caller handles 401 itself (e.g. the session probe) — surface it as an
      // ApiError instead of redirecting.
      throw new ApiError(401, "Unauthenticated");
    }
    // Refresh failed (no/expired/revoked refresh token) — send user back to
    // landing/login.
    window.location.replace("/");
    // Return a never-resolving promise so callers don't continue processing.
    return new Promise(() => {});
  }

  if (!response.ok) {
    let errorBody: unknown;
    try {
      errorBody = await response.json();
    } catch {
      errorBody = await response.text();
    }
    const message =
      typeof errorBody === "object" &&
      errorBody !== null &&
      "message" in errorBody
        ? String((errorBody as { message: unknown }).message)
        : `HTTP ${response.status}`;
    throw new ApiError(response.status, message, errorBody);
  }

  // 204 No Content — return undefined cast to T
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export const apiClient = {
  get<T>(path: string, options?: Omit<RequestOptions, "method" | "body">) {
    return request<T>(path, { ...options, method: "GET" });
  },

  post<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) {
    return request<T>(path, { ...options, method: "POST", body });
  },

  put<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) {
    return request<T>(path, { ...options, method: "PUT", body });
  },

  patch<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">) {
    return request<T>(path, { ...options, method: "PATCH", body });
  },

  delete<T>(path: string, options?: Omit<RequestOptions, "method">) {
    return request<T>(path, { ...options, method: "DELETE" });
  },
};
